import React, { useState } from "react";
import axios from "axios";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

const residencyOptions = [
  {
    icon: "fas fa-award",
    eyebrow: "Long-term residency",
    title: "10-Year Golden Visa",
    value: "AED 2M+",
    valueLabel: "Qualifying property investment",
    description:
      "For eligible real estate investors, the Golden Visa offers a 10-year residency pathway and is commonly associated with qualifying property investment of AED 2 million or more.",
    features: [
      "Long-term residency pathway",
      "Support for qualifying property investors",
      "Application and document guidance",
    ],
  },
  {
    icon: "far fa-building",
    eyebrow: "Property owner pathway",
    title: "2-Year Property Owner Residency",
    value: "2 Years",
    valueLabel: "Renewable residency route",
    description:
      "Property owners may also be eligible for a 2-year residency pathway, subject to applicable requirements, current regulations, and approval by the relevant authorities.",
    features: [
      "Designed for eligible property owners",
      "Clear eligibility review",
      "Professional application coordination",
    ],
  },
];

const supportSteps = [
  {
    icon: "fas fa-search",
    title: "Review your case",
    text: "We assess your property and circumstances, then explain the available residency pathway.",
  },
  {
    icon: "far fa-file-alt",
    title: "Prepare documents",
    text: "Our team guides you through the required documents and supporting paperwork.",
  },
  {
    icon: "fas fa-tasks",
    title: "Coordinate the application",
    text: "We assist with preparation, submission steps, follow-up, and clear communication.",
  },
  {
    icon: "far fa-check-circle",
    title: "Move forward confidently",
    text: "You stay informed at each stage while we help keep the process moving efficiently.",
  },
];

function GoldenVisa() {
  const API = process.env.REACT_APP_API_URL || "https://nvrealestate.ae";
  const initialFormData = {
    agent_name: "",
    agent_phone: "",
    agent_email: "",
    agent_address: "",
  };
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [response, setResponse] = useState({ text: "", type: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: "" }));
  };

  const handlePhoneChange = (phone) => {
    setFormData((current) => ({ ...current, agent_phone: phone }));
    setErrors((current) => ({ ...current, agent_phone: "" }));
  };

  const validate = () => {
    const nextErrors = {};
    const phoneDigits = formData.agent_phone.replace(/\D/g, "");

    if (!formData.agent_name.trim()) nextErrors.agent_name = "Name is required";
    if (!phoneDigits) nextErrors.agent_phone = "Phone number is required";
    else if (phoneDigits.length < 7 || phoneDigits.length > 15) {
      nextErrors.agent_phone = "Enter a valid phone number";
    }
    if (!formData.agent_email.trim()) nextErrors.agent_email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.agent_email)) {
      nextErrors.agent_email = "Enter a valid email address";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setResponse({ text: "", type: "" });
    if (!validate()) return;

    setSubmitting(true);
    try {
      const payload = {
        proref: "Golden Visa & Property Residency",
        current_url: window.location.href,
        agent_name: formData.agent_name,
        agent_lname: "-",
        agent_phone: formData.agent_phone,
        agent_email: formData.agent_email,
        agent_address: formData.agent_address,
        enqtyp: "Golden Visa Eligibility Enquiry",
      };

      const result = await axios.post(
        `${API}/api/EnquiryHandlerStatic.ashx`,
        JSON.stringify(payload),
        { headers: { "Content-Type": "application/json" } }
      );

      setResponse({
        text: result.data?.message || "Thank you. Our residency advisor will contact you shortly.",
        type: "success",
      });
      setFormData(initialFormData);
      setErrors({});
    } catch (error) {
      setResponse({ text: "Something went wrong. Please try again.", type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <style>{`
        .nv-visa-page {
          --visa-primary: #293e95;
          --visa-heading: #09256d;
          --visa-dark: #071634;
          --visa-ink: #151b2b;
          --visa-muted: #667085;
          --visa-border: #e1e6ef;
          --visa-soft: #f4f6fc;
          color: var(--visa-ink);
          background: #fff;
        }

        .nv-visa-shell {
          width: min(1180px, calc(100% - 32px));
          margin: 0 auto;
        }

        .nv-visa-hero {
          position: relative;
          min-height: 700px;
          overflow: hidden;
          color: var(--visa-ink);
          background: #f4f6fc;
        }

        .nv-visa-hero::before {
          content: "";
          position: absolute;
          top: -180px;
          left: -160px;
          width: 470px;
          height: 470px;
          border: 1px solid rgba(41, 62, 149, .1);
          border-radius: 50%;
          box-shadow:
            0 0 0 55px rgba(41, 62, 149, .025),
            0 0 0 110px rgba(41, 62, 149, .018);
          pointer-events: none;
        }

        .nv-visa-hero-layout {
          position: relative;
          z-index: 1;
          min-height: 700px;
          display: grid;
          grid-template-columns: .92fr 1.08fr;
          gap: 60px;
          align-items: center;
          padding: 112px 0 130px;
        }

        .nv-visa-hero-copy {
          max-width: 610px;
        }

        .nv-visa-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 9px;
          margin-bottom: 18px;
          padding: 8px 12px;
          border: 1px solid #d9def3;
          border-radius: 999px;
          background: #fff;
          color: var(--visa-primary);
          font-size: 11px;
          font-weight: 800;
          letter-spacing: .13em;
          text-transform: uppercase;
        }

        .nv-visa-eyebrow::before {
          content: "";
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: var(--visa-primary);
          box-shadow: 0 0 0 4px rgba(41, 62, 149, .1);
        }

        .nv-visa-hero h1 {
          max-width: 600px;
          margin: 0 0 22px;
          color: var(--visa-dark);
          font-size: clamp(43px, 5vw, 68px);
          line-height: 1.03;
          letter-spacing: -.045em;
        }

        .nv-visa-hero h1 span {
          position: relative;
          color: var(--visa-primary);
        }

        .nv-visa-hero p {
          max-width: 590px;
          margin: 0 0 28px;
          color: #4a5b80;
          font-size: 16px;
          line-height: 1.7;
        }

        .nv-visa-hero .nv-visa-btn.outline {
          border-color: #c8d0e8;
          background: #fff;
          color: var(--visa-primary);
        }

        .nv-visa-hero .nv-visa-btn.outline:hover {
          border-color: var(--visa-primary);
          background: var(--visa-primary);
          color: #fff;
        }

        .nv-visa-hero-proof {
          display: flex;
          flex-wrap: wrap;
          gap: 18px;
          margin-top: 28px;
          padding-top: 23px;
          border-top: 1px solid #dce1ee;
        }

        .nv-visa-hero-proof span {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          color: #53617d;
          font-size: 11px;
          font-weight: 700;
        }

        .nv-visa-hero-proof i { color: var(--visa-primary); }

        .nv-visa-hero-visual {
          position: relative;
          min-height: 520px;
        }

        .nv-visa-hero-photo {
          position: absolute;
          inset: 0 0 0 55px;
          overflow: hidden;
          border-radius: 170px 18px 18px 18px;
          background:
            linear-gradient(180deg, rgba(7,22,52,.03), rgba(7,22,52,.28)),
            url("/assets/images/abu.jpg") center 60% / cover no-repeat;
          box-shadow: 0 25px 60px rgba(24,35,68,.2);
        }

        .nv-visa-hero-photo::after {
          content: "";
          position: absolute;
          inset: 0;
          border: 1px solid rgba(255,255,255,.25);
          border-radius: inherit;
        }

        .nv-visa-hero-seal {
          position: absolute;
          top: 30px;
          left: 0;
          width: 116px;
          height: 116px;
          display: grid;
          place-items: center;
          border: 8px solid #f4f6fc;
          border-radius: 50%;
          background: var(--visa-primary);
          color: #fff;
          text-align: center;
          box-shadow: 0 14px 30px rgba(41,62,149,.25);
        }

        .nv-visa-hero-seal strong {
          display: block;
          font-size: 27px;
          line-height: 1;
        }

        .nv-visa-hero-seal span {
          display: block;
          margin-top: 4px;
          font-size: 9px;
          font-weight: 800;
          letter-spacing: .08em;
          text-transform: uppercase;
        }

        .nv-visa-hero-pathways {
          position: absolute;
          right: -20px;
          bottom: 28px;
          width: min(355px, calc(100% - 40px));
          overflow: hidden;
          border: 1px solid rgba(255,255,255,.75);
          border-radius: 12px;
          background: rgba(255,255,255,.94);
          box-shadow: 0 18px 40px rgba(7,22,52,.2);
          backdrop-filter: blur(12px);
        }

        .nv-visa-hero-pathway {
          display: grid;
          grid-template-columns: 43px 1fr auto;
          align-items: center;
          gap: 12px;
          padding: 16px 17px;
        }

        .nv-visa-hero-pathway + .nv-visa-hero-pathway {
          border-top: 1px solid var(--visa-border);
        }

        .nv-visa-hero-pathway > i {
          width: 43px;
          height: 43px;
          display: grid;
          place-items: center;
          border-radius: 50%;
          background: #edf0ff;
          color: var(--visa-primary);
        }

        .nv-visa-hero-pathway strong {
          display: block;
          margin-bottom: 2px;
          color: var(--visa-heading);
          font-size: 13px;
        }

        .nv-visa-hero-pathway span {
          color: var(--visa-muted);
          font-size: 10px;
        }

        .nv-visa-hero-pathway b {
          color: var(--visa-primary);
          font-size: 14px;
          white-space: nowrap;
        }

        .nv-visa-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
        }

        .nv-visa-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 9px;
          min-height: 50px;
          padding: 12px 22px;
          border: 1px solid var(--visa-primary);
          border-radius: 6px;
          background: #1f317b;
          color: #fff;
          font-size: 13px;
          font-weight: 800;
          text-decoration: none;
          text-transform: uppercase;
          transition: .2s ease;
          cursor: pointer;
        }

        .nv-visa-btn:hover {
          transform: translateY(-2px);
          border-color: #1f317b;
          background: #1f317b;
          color: #fff;
        }

        .nv-visa-btn.outline {
          border-color: rgba(255,255,255,.68);
          background: rgba(255,255,255,.08);
          color: #fff;
        }

        .nv-visa-btn.outline:hover {
          border-color: #fff;
          background: #fff;
          color: var(--visa-primary);
        }

        .nv-visa-intro-card {
          position: relative;
          z-index: 3;
          display: grid;
          grid-template-columns: 88px 1fr auto;
          align-items: center;
          gap: 22px;
          margin-top: -66px;
          padding: 28px 30px;
          border: 1px solid var(--visa-border);
          border-radius: 12px;
          background: #fff;
          box-shadow: 0 18px 42px rgba(24, 35, 68, .13);
        }

        .nv-visa-intro-icon {
          width: 70px;
          height: 70px;
          display: grid;
          place-items: center;
          border-radius: 50%;
          background: var(--visa-primary);
          color: #fff;
          font-size: 28px;
        }

        .nv-visa-intro-card h2 {
          margin: 0 0 7px;
          color: var(--visa-heading);
          font-size: 24px;
          line-height: 1.3;
        }

        .nv-visa-intro-card p {
          max-width: 710px;
          margin: 0;
          color: #4a5b80;
          font-size: 14px;
          line-height: 1.7;
        }

        .nv-visa-section { padding: 84px 0; }
        .nv-visa-section.soft { background: var(--visa-soft); }

        .nv-visa-section-title {
          max-width: 760px;
          margin: 0 auto 38px;
          text-align: center;
        }

        .nv-visa-section-title span {
          display: block;
          margin-bottom: 8px;
          color: var(--visa-primary);
          font-size: 12px;
          font-weight: 800;
          letter-spacing: .15em;
          text-transform: uppercase;
        }

        .nv-visa-section-title h2 {
          margin: 0 0 12px;
          color: var(--visa-heading);
          font-size: clamp(30px, 3vw, 42px);
          line-height: 1.2;
          letter-spacing: -.025em;
        }

        .nv-visa-section-title p {
          margin: 0;
          color: #4a5b80;
          font-size: 15px;
          line-height: 1.75;
        }

        .nv-visa-options {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 24px;
        }

        .nv-visa-option {
          position: relative;
          overflow: hidden;
          padding: 34px;
          border: 1px solid var(--visa-border);
          border-radius: 12px;
          background: #fff;
          box-shadow: 0 14px 38px rgba(24, 35, 68, .08);
        }

        .nv-visa-option::before {
          content: "";
          position: absolute;
          inset: 0 auto 0 0;
          width: 5px;
          background: var(--visa-primary);
        }

        .nv-visa-option-head {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 24px;
        }

        .nv-visa-option-icon {
          flex: 0 0 58px;
          width: 58px;
          height: 58px;
          display: grid;
          place-items: center;
          border-radius: 50%;
          background: #edf0ff;
          color: var(--visa-primary);
          font-size: 23px;
        }

        .nv-visa-option-eyebrow {
          display: block;
          margin-bottom: 4px;
          color: var(--visa-primary);
          font-size: 11px;
          font-weight: 800;
          letter-spacing: .1em;
          text-transform: uppercase;
        }

        .nv-visa-option h3 {
          margin: 0;
          color: var(--visa-heading);
          font-size: 23px;
          line-height: 1.3;
        }

        .nv-visa-investment {
          display: flex;
          align-items: baseline;
          gap: 10px;
          margin-bottom: 18px;
          padding: 16px 18px;
          border-radius: 8px;
          background: var(--visa-soft);
        }

        .nv-visa-investment strong {
          color: var(--visa-primary);
          font-size: 30px;
          line-height: 1;
        }

        .nv-visa-investment span {
          color: var(--visa-muted);
          font-size: 12px;
          font-weight: 700;
        }

        .nv-visa-option > p {
          margin: 0 0 20px;
          color: #4a5b80;
          font-size: 14px;
          line-height: 1.75;
        }

        .nv-visa-checks {
          margin: 0;
          padding: 0;
          list-style: none;
        }

        .nv-visa-checks li {
          display: flex;
          gap: 10px;
          margin: 10px 0;
          color: #30394b;
          font-size: 13px;
        }

        .nv-visa-checks i { color: var(--visa-primary); }

        .nv-visa-guidance {
          display: grid;
          grid-template-columns: .9fr 1.1fr;
          gap: 54px;
          align-items: center;
        }

        .nv-visa-guidance-image {
          position: relative;
          min-height: 480px;
          overflow: hidden;
          border-radius: 12px;
          background:
            linear-gradient(180deg, rgba(7, 22, 52, .08), rgba(7, 22, 52, .48)),
            url("/assets/images/golden-visa.jpg") center / cover no-repeat;
          box-shadow: 0 18px 42px rgba(24, 35, 68, .14);
        }

        .nv-visa-guidance-badge {
          position: absolute;
          right: 22px;
          bottom: 22px;
          max-width: 230px;
          padding: 20px;
          border-radius: 9px;
          background:linear-gradient(145deg, #abbde3 0%, #d5ddf0 82%);
          box-shadow: 0 12px 28px rgba(7, 22, 52, .18);
        }

        .nv-visa-guidance-badge strong {
          display: block;
          margin-bottom: 6px;
          color: var(--visa-heading);
          font-size: 18px;
        }

        .nv-visa-guidance-badge span {
          color: var(--visa-muted);
          font-size: 12px;
          line-height: 1.55;
        }

        .nv-visa-guidance-copy > span {
          display: block;
          margin-bottom: 9px;
          color: var(--visa-primary);
          font-size: 12px;
          font-weight: 800;
          letter-spacing: .14em;
          text-transform: uppercase;
        }

        .nv-visa-guidance-copy h2 {
          margin: 0 0 17px;
          color: var(--visa-heading);
          font-size: clamp(31px, 3.4vw, 44px);
          line-height: 1.2;
          letter-spacing: -.025em;
        }

        .nv-visa-guidance-copy > p {
          margin: 0 0 26px;
          color: #4a5b80;
          font-size: 15px;
          line-height: 1.75;
        }

        .nv-visa-points {
          display: grid;
          gap: 14px;
          margin-bottom: 28px;
        }

        .nv-visa-point {
          display: flex;
          align-items: flex-start;
          gap: 13px;
        }

        .nv-visa-point i {
          flex: 0 0 38px;
          width: 38px;
          height: 38px;
          display: grid;
          place-items: center;
          border-radius: 50%;
          background: #edf0ff;
          color: var(--visa-primary);
        }

        .nv-visa-point strong {
          display: block;
          margin-bottom: 3px;
          color: var(--visa-heading);
          font-size: 14px;
        }

        .nv-visa-point span {
          color: var(--visa-muted);
          font-size: 12px;
          line-height: 1.55;
        }

        .nv-visa-steps {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 18px;
        }

        .nv-visa-step {
          position: relative;
          padding: 28px 23px;
          border: 1px solid var(--visa-border);
          border-radius: 10px;
          background: #fff;
          box-shadow: 0 10px 30px rgba(24, 35, 68, .06);
        }

        .nv-visa-step-number {
          position: absolute;
          top: 18px;
          right: 20px;
          color: #dce2f7;
          font-size: 28px;
          font-weight: 900;
        }

        .nv-visa-step-icon {
          width: 48px;
          height: 48px;
          display: grid;
          place-items: center;
          margin-bottom: 18px;
          border-radius: 50%;
          background: var(--visa-primary);
          color: #fff;
          font-size: 18px;
        }

        .nv-visa-step h3 {
          margin: 0 0 9px;
          color: var(--visa-heading);
          font-size: 18px;
          line-height: 1.35;
        }

        .nv-visa-step p {
          margin: 0;
          color: var(--visa-muted);
          font-size: 12px;
          line-height: 1.65;
        }

        .nv-visa-why {
          display: grid;
          grid-template-columns: 1.1fr .9fr;
          align-items: center;
          gap: 36px;
          padding: 42px;
          border-radius: 12px;
          color: #fff;
          background:
            linear-gradient(90deg, rgba(5, 18, 46, .98), rgba(9, 28, 68, .89)),
            url("/assets/images/why.jpg") center / cover no-repeat;
        }

        .nv-visa-why h2 {
          margin: 0 0 12px;
          color: #fff;
          font-size: clamp(28px, 3vw, 39px);
          line-height: 1.2;
        }

        .nv-visa-why p {
          margin: 0;
          color: rgba(255,255,255,.79);
          font-size: 14px;
          line-height: 1.75;
        }

        .nv-visa-why-list {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 12px;
        }

        .nv-visa-why-list span {
          display: flex;
          align-items: center;
          gap: 9px;
          padding: 13px;
          border: 1px solid rgba(255,255,255,.16);
          border-radius: 7px;
          background: rgba(255,255,255,.07);
          font-size: 12px;
          font-weight: 700;
        }

        .nv-visa-why-list i { color: #aebcff; }

        .nv-visa-bottom {
          padding: 62px 0;
          color: #fff;
          background: var(--visa-dark);
        }

        .nv-visa-bottom-inner {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 34px;
        }

        .nv-visa-bottom h2 {
          margin: 0 0 8px;
          color: #fff;
          font-size: 32px;
        }

        .nv-visa-bottom p {
          max-width: 650px;
          margin: 0;
          color: rgba(255,255,255,.76);
          font-size: 14px;
          line-height: 1.65;
        }

        .nv-visa-modal .modal-dialog { max-width: 520px; }

        .nv-visa-modal .modal-content {
          overflow: hidden;
          padding: 0 !important;
          border: 0;
          border-radius: 16px;
          background: #fff;
          box-shadow: 0 24px 70px rgba(7, 22, 52, .28);
          text-align: left !important;
        }

        .nv-visa-modal .modal-header {
          align-items: flex-start;
          padding: 27px 28px 24px;
          border: 0;
          color: #fff;
          background:
            linear-gradient(110deg, rgba(5, 18, 46, .98), rgba(41, 62, 149, .92)),
            url("/assets/images/abu.jpg") center / cover no-repeat;
        }

        .nv-visa-modal-heading { padding-right: 24px; }

        .nv-visa-modal-heading span {
          display: block;
          margin-bottom: 7px;
          color: #aebcff;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: .14em;
          text-transform: uppercase;
        }

        .nv-visa-modal .modal-title {
          margin: 0 0 7px;
          color: #fff;
          font-size: 25px;
          line-height: 1.25;
        }

        .nv-visa-modal-heading p {
          margin: 0;
          color: rgba(255,255,255,.78);
          font-size: 12px;
          line-height: 1.55;
        }

        .nv-visa-modal .btn-close {
          flex: 0 0 auto;
          margin: 0;
          padding: 8px;
          border-radius: 50%;
          background-color: #fff;
          opacity: .9;
        }

        .nv-visa-modal-form {
          display: grid;
          gap: 15px;
          padding: 27px 28px 30px;
        }

        .nv-visa-modal-field label {
          display: block;
          margin-bottom: 7px;
          color: #293143;
          font-size: 12px;
          font-weight: 700;
        }

        .nv-visa-modal-field input,
        .nv-visa-modal-field textarea,
        .nv-visa-modal-field .phone-input {
          width: 100% !important;
          min-height: 49px;
          padding: 11px 13px;
          border: 1px solid #d6dce7 !important;
          border-radius: 7px !important;
          background: #fff;
          color: var(--visa-ink);
          font-size: 13px;
          outline: none;
          box-shadow: none !important;
        }

        .nv-visa-modal-field .react-tel-input .form-control { padding-left: 48px; }

        .nv-visa-modal-field .react-tel-input .flag-dropdown {
          border: 1px solid #d6dce7;
          border-radius: 7px 0 0 7px;
          background: #f7f8fb;
        }

        .nv-visa-modal-field textarea {
          min-height: 100px;
          resize: vertical;
        }

        .nv-visa-modal-field input:focus,
        .nv-visa-modal-field textarea:focus {
          border-color: var(--visa-primary) !important;
          box-shadow: 0 0 0 3px rgba(41, 62, 149, .10) !important;
        }

        .nv-visa-modal-field .input-error { border-color: #d92d20 !important; }

        .nv-visa-modal-error {
          margin: 5px 0 0;
          color: #d92d20;
          font-size: 11px;
        }

        .nv-visa-modal-response {
          margin: 0;
          padding: 11px 13px;
          border-radius: 6px;
          font-size: 12px;
        }

        .nv-visa-modal-response.success { background: #ecfdf3; color: #027a48; }
        .nv-visa-modal-response.error { background: #fef3f2; color: #b42318; }

        .nv-visa-modal-submit {
          width: 100%;
          border: 0;
        }

        .nv-visa-modal-submit:disabled {
          cursor: wait;
          opacity: .7;
        }

        @media (max-width: 1000px) {
          .nv-visa-hero-layout { gap: 28px; }
          .nv-visa-hero-visual { min-height: 470px; }
          .nv-visa-hero-photo { left: 35px; }
          .nv-visa-intro-card { grid-template-columns: 74px 1fr; }
          .nv-visa-intro-card .nv-visa-btn { grid-column: 1 / -1; }
          .nv-visa-guidance { grid-template-columns: 1fr 1fr; gap: 32px; }
          .nv-visa-steps { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .nv-visa-why { grid-template-columns: 1fr; }
        }

        @media (max-width: 760px) {
          .nv-visa-hero { min-height: auto; }
          .nv-visa-hero-layout {
            min-height: auto;
            grid-template-columns: 1fr;
            gap: 42px;
            padding: 100px 0 125px;
          }
          .nv-visa-hero-copy { max-width: 680px; }
          .nv-visa-hero-visual { min-height: 500px; }
          .nv-visa-hero-photo { inset: 0 20px 0 55px; }
          .nv-visa-hero-pathways { right: 0; }
          .nv-visa-options, .nv-visa-guidance { grid-template-columns: 1fr; }
          .nv-visa-guidance-image { min-height: 390px; }
          .nv-visa-section { padding: 66px 0; }
          .nv-visa-bottom-inner { align-items: flex-start; flex-direction: column; }
        }

        @media (max-width: 540px) {
          .nv-visa-shell { width: min(100% - 22px, 1180px); }
          .nv-visa-hero h1 { font-size: 42px; }
          .nv-visa-hero-layout { padding-top: 88px; }
          .nv-visa-hero-proof { gap: 11px 16px; }
          .nv-visa-hero-visual { min-height: 430px; }
          .nv-visa-hero-photo {
            inset: 0 0 0 25px;
            border-radius: 110px 14px 14px 14px;
          }
          .nv-visa-hero-seal {
            top: 18px;
            width: 92px;
            height: 92px;
            border-width: 6px;
          }
          .nv-visa-hero-seal strong { font-size: 22px; }
          .nv-visa-hero-pathways {
            right: 8px;
            bottom: 16px;
            width: calc(100% - 28px);
          }
          .nv-visa-hero-pathway {
            grid-template-columns: 38px 1fr auto;
            padding: 13px;
          }
          .nv-visa-hero-pathway > i { width: 38px; height: 38px; }
          .nv-visa-actions { flex-direction: column; width: 100%; }
          .nv-visa-actions .nv-visa-btn { width: 100%; }
          .nv-visa-intro-card { grid-template-columns: 1fr; padding: 24px; }
          .nv-visa-intro-card .nv-visa-btn { grid-column: auto; }
          .nv-visa-option { padding: 27px 24px; }
          .nv-visa-option-head { align-items: flex-start; }
          .nv-visa-investment { align-items: flex-start; flex-direction: column; }
          .nv-visa-steps, .nv-visa-why-list { grid-template-columns: 1fr; }
          .nv-visa-why { padding: 30px 24px; }
          .nv-visa-guidance-badge { right: 14px; bottom: 14px; left: 14px; max-width: none; }
          .nv-visa-modal .modal-header,
          .nv-visa-modal-form { padding-left: 20px; padding-right: 20px; }
        }
      `}</style>

      <main className="nv-visa-page">
        <section className="nv-visa-hero">
          <div className="nv-visa-shell nv-visa-hero-layout">
            <div className="nv-visa-hero-copy">
              <span className="nv-visa-eyebrow">Abu Dhabi residency services</span>
              <h1>
                Residency by <span>Property Investment</span>
              </h1>
              <p>
                Professional support for clients seeking residency in Abu Dhabi through qualifying property ownership.
Explore your residency options with a team focused on process guidance, documentation support, application coordination, and clear follow-up from start to finish.

              </p>
              <div className="nv-visa-actions">
                <button
                  className="nv-visa-btn"
                  type="button"
                  data-bs-toggle="modal"
                  data-bs-target="#goldenVisaEnquiryModal"
                >
                  <i className="far fa-check-circle" /> Check Your Eligibility
                </button>
                <a
                  className="nv-visa-btn outline"
                  href="https://wa.me/971501577822"
                  target="_blank"
                  rel="noreferrer"
                >
                  <i className="fab fa-whatsapp" /> Speak to an Advisor
                </a>
              </div>
              <div className="nv-visa-hero-proof" aria-label="Residency service benefits">
                <span><i className="fas fa-check-circle" /> Eligibility guidance</span>
                <span><i className="fas fa-check-circle" /> Document support</span>
                <span><i className="fas fa-check-circle" /> Application follow-up</span>
              </div>
            </div>

            <div className="nv-visa-hero-visual" aria-label="Property residency pathways">
              <div className="nv-visa-hero-photo" role="img" aria-label="Abu Dhabi skyline" />
              <div className="nv-visa-hero-seal">
                <div>
                  <strong>10</strong>
                  <span>Year Visa</span>
                </div>
              </div>
              <div className="nv-visa-hero-pathways">
                <div className="nv-visa-hero-pathway">
                  <i className="fas fa-award" />
                  <div>
                    <strong>Golden Visa</strong>
                    <span>Long-term investor residency</span>
                  </div>
                  <b>AED 2M+</b>
                </div>
                <div className="nv-visa-hero-pathway">
                  <i className="far fa-building" />
                  <div>
                    <strong>Property Owner Residency</strong>
                    <span>Alternative ownership pathway</span>
                  </div>
                  <b>2 Years</b>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="nv-visa-shell">
          <section className="nv-visa-intro-card" aria-label="Residency support introduction">
            <span className="nv-visa-intro-icon">
              <i className="fas fa-passport" />
            </span>
            <div>
              <h2>Clear, professional support from start to finish</h2>
              <p>
               Our team provides clear and professional support throughout the residency process linked to property ownership in Abu Dhabi. From document guidance and application support to follow-up and coordination, we help make the process smoother, faster, and easier to manage.
              </p>
            </div>
            <button
              className="nv-visa-btn"
              type="button"
              data-bs-toggle="modal"
              data-bs-target="#goldenVisaEnquiryModal"
            >
              Enquire Now <i className="fas fa-arrow-right" />
            </button>
          </section>
        </div>

        <section className="nv-visa-section">
          <div className="nv-visa-shell">
            <header className="nv-visa-section-title">
              <span>Residency options</span>
              <h2>Choose the pathway that fits your property investment</h2>
              <p>
                Eligibility depends on the property, investment value, current
                regulations, and approval by the relevant authorities.
              </p>
            </header>

            <div className="nv-visa-options">
              {residencyOptions.map((option) => (
                <article className="nv-visa-option" key={option.title}>
                  <div className="nv-visa-option-head">
                    <span className="nv-visa-option-icon">
                      <i className={option.icon} />
                    </span>
                    <div>
                      <span className="nv-visa-option-eyebrow">{option.eyebrow}</span>
                      <h3>{option.title}</h3>
                    </div>
                  </div>
                  <div className="nv-visa-investment">
                    <strong>{option.value}</strong>
                    <span>{option.valueLabel}</span>
                  </div>
                  <p>{option.description}</p>
                  <ul className="nv-visa-checks">
                    {option.features.map((feature) => (
                      <li key={feature}>
                        <i className="fas fa-check-circle" /> {feature}
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="nv-visa-section soft">
          <div className="nv-visa-shell nv-visa-guidance">
            <div className="nv-visa-guidance-image" aria-hidden="true">
              <div className="nv-visa-guidance-badge">
                <strong>The right path, clearly explained</strong>
                <span>Practical guidance tailored to your property and residency goals.</span>
              </div>
            </div>
            <div className="nv-visa-guidance-copy">
              <span>Clear guidance</span>
              <h2>Structure, documents, and follow-up all matter</h2>
              <p>
                Whether you are applying under the Golden Visa route or the
                2-year property owner residency route, our role is to give you
                practical support and keep every stage clear.
              </p>
              <div className="nv-visa-points">
                <div className="nv-visa-point">
                  <i className="fas fa-route" />
                  <div>
                    <strong>Pathway clarity</strong>
                    <span>Understand the residency option available for your case.</span>
                  </div>
                </div>
                <div className="nv-visa-point">
                  <i className="far fa-folder-open" />
                  <div>
                    <strong>Document readiness</strong>
                    <span>Know what paperwork is required before the application moves forward.</span>
                  </div>
                </div>
                <div className="nv-visa-point">
                  <i className="fas fa-headset" />
                  <div>
                    <strong>Responsive support</strong>
                    <span>Receive clear communication and practical follow-up throughout.</span>
                  </div>
                </div>
              </div>
              <button
                className="nv-visa-btn"
                type="button"
                data-bs-toggle="modal"
                data-bs-target="#goldenVisaEnquiryModal"
              >
                Start Your Residency Process
              </button>
            </div>
          </div>
        </section>

        <section className="nv-visa-section">
          <div className="nv-visa-shell">
            <header className="nv-visa-section-title">
              <span>How we help</span>
              <h2>Support at every stage of the residency process</h2>
              <p>
                A straightforward process built around preparation, coordination,
                and consistent communication.
              </p>
            </header>
            <div className="nv-visa-steps">
              {supportSteps.map((step, index) => (
                <article className="nv-visa-step" key={step.title}>
                  <span className="nv-visa-step-number">0{index + 1}</span>
                  <span className="nv-visa-step-icon">
                    <i className={step.icon} />
                  </span>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="nv-visa-section soft">
          <div className="nv-visa-shell">
            <div className="nv-visa-why">
              <div>
                <h2>Why clients choose this service</h2>
                <p>
                  Clients want more than general information. They want practical
                  support, fast responses, and a professional team that can help
                  them navigate the process correctly.
                </p>
              </div>
              <div className="nv-visa-why-list">
                <span><i className="fas fa-check" /> Practical guidance</span>
                <span><i className="fas fa-check" /> Fast responses</span>
                <span><i className="fas fa-check" /> Clear communication</span>
                <span><i className="fas fa-check" /> Professional follow-up</span>
              </div>
            </div>
          </div>
        </section>

        <section className="nv-visa-bottom">
          <div className="nv-visa-shell nv-visa-bottom-inner">
            <div>
              <h2>Check your eligibility today</h2>
              <p>
                Speak with our team to explore your residency options through
                property ownership in Abu Dhabi and get professional application support.
              </p>
            </div>
            <div className="nv-visa-actions">
              <button
                className="nv-visa-btn"
                type="button"
                data-bs-toggle="modal"
                data-bs-target="#goldenVisaEnquiryModal"
              >
                Enquire Now
              </button>
              <a
                className="nv-visa-btn outline"
                href="https://wa.me/971501577822"
                target="_blank"
                rel="noreferrer"
              >
                <i className="fab fa-whatsapp" /> Speak to an Advisor
              </a>
            </div>
          </div>
        </section>
      </main>

      <div
        className="modal fade nv-visa-modal"
        id="goldenVisaEnquiryModal"
        tabIndex={-1}
        aria-labelledby="goldenVisaEnquiryModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <div className="nv-visa-modal-heading">
                <span>Residency consultation</span>
                <h2 className="modal-title" id="goldenVisaEnquiryModalLabel">
                  Check Your Eligibility
                </h2>
                <p>Share your details and our property residency team will contact you.</p>
              </div>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>

            <form className="nv-visa-modal-form" onSubmit={handleSubmit} noValidate>
              <div className="nv-visa-modal-field">
                <label htmlFor="visa-enquiry-name">Full Name *</label>
                <input
                  id="visa-enquiry-name"
                  name="agent_name"
                  value={formData.agent_name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  className={errors.agent_name ? "input-error" : ""}
                />
                {errors.agent_name && <p className="nv-visa-modal-error">{errors.agent_name}</p>}
              </div>

              <div className="nv-visa-modal-field">
                <label htmlFor="visa-enquiry-phone">Phone Number *</label>
                <PhoneInput
                  country="ae"
                  value={formData.agent_phone}
                  onChange={handlePhoneChange}
                  inputClass={`phone-input ${errors.agent_phone ? "input-error" : ""}`}
                  containerClass="phone-container"
                  enableSearch
                  inputProps={{ id: "visa-enquiry-phone" }}
                  placeholder="Enter phone number"
                />
                {errors.agent_phone && <p className="nv-visa-modal-error">{errors.agent_phone}</p>}
              </div>

              <div className="nv-visa-modal-field">
                <label htmlFor="visa-enquiry-email">Email Address *</label>
                <input
                  id="visa-enquiry-email"
                  type="email"
                  name="agent_email"
                  value={formData.agent_email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  className={errors.agent_email ? "input-error" : ""}
                />
                {errors.agent_email && <p className="nv-visa-modal-error">{errors.agent_email}</p>}
              </div>

              <div className="nv-visa-modal-field">
                <label htmlFor="visa-enquiry-message">Message</label>
                <textarea
                  id="visa-enquiry-message"
                  name="agent_address"
                  value={formData.agent_address}
                  onChange={handleChange}
                  placeholder="Tell us about your property or residency requirements..."
                />
              </div>

              {response.text && (
                <p className={`nv-visa-modal-response ${response.type}`} role="status">
                  {response.text}
                </p>
              )}

              <button
                className="nv-visa-btn nv-visa-modal-submit"
                type="submit"
                disabled={submitting}
              >
                {submitting ? "Sending..." : "Submit Enquiry"}
                {!submitting && <i className="fas fa-arrow-right" />}
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

export default GoldenVisa;
